import React, { memo, useCallback, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import Button from "@mui/material/Button";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import { useFormState } from "react-final-form";

/**
 * PdfDownloadButton
 *
 * Usage (explicit values wiring):
 * <PdfDownloadButton filename="letter-of-intent.pdf">
 *   <LoanTermsSheetPDF formData={values} />
 * </PdfDownloadButton>
 *
 * Usage (auto-inject Final Form values into child as formData):
 * <PdfDownloadButton filename="letter-of-intent.pdf" injectFinalFormValues>
 *   <LoanTermsSheetPDF />
 * </PdfDownloadButton>
 *
 * Usage (auto-inject into a different prop name):
 * <PdfDownloadButton filename="doc.pdf" injectFinalFormValues injectPropName="values">
 *   <SomePdfComponent />
 * </PdfDownloadButton>
 */
const PdfDownloadButton = memo(
  ({
    children,
    filename = "document.pdf",
    injectFinalFormValues = false,
    injectPropName = "formData",
    buttonText = "Download (PDF)",
    startIcon = <FileDownloadOutlined className="thin" />,
    onBeforeDownload, // optional async hook: () => void|Promise<void>
    onAfterDownload, // optional: ({ filename, blob }) => void
    onError, // optional: (err) => void
    ...buttonProps // any MUI Button props (variant, size, sx, etc.)
  }) => {
    const { values } = useFormState() || {};
    const [loading, setLoading] = useState(false);

    const pdfElement = useMemo(() => {
      if (!React.isValidElement(children)) return null;

      if (!injectFinalFormValues) return children;

      // Inject final-form values into the child under injectPropName (default: formData)
      return React.cloneElement(children, { [injectPropName]: values });
    }, [children, injectFinalFormValues, injectPropName, values]);

    const download = useCallback(async () => {
      try {
        if (!pdfElement) {
          throw new Error(
            "PdfDownloadButton requires a single valid React element child (your React-PDF <Document /> tree)."
          );
        }

        setLoading(true);

        if (onBeforeDownload) {
          await onBeforeDownload();
        }

        const blob = await pdf(pdfElement).toBlob();
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        if (onAfterDownload) {
          onAfterDownload({ filename, blob });
        }
      } catch (err) {
        if (onError) onError(err);
        // Keep console error for dev visibility; remove if you prefer silent failures
        console.error("Error generating/downloading PDF:", err);
      } finally {
        setLoading(false);
      }
    }, [pdfElement, filename, onBeforeDownload, onAfterDownload, onError]);

    return (
      <Button
        onClick={download}
        disabled={loading || buttonProps.disabled}
        startIcon={startIcon}
        {...buttonProps}
      >
        {loading ? "Generating..." : buttonText}
      </Button>
    );
  }
);

export default PdfDownloadButton;
